import { useMemo, useState } from 'react';

import DashboardLayout from '@/components/layout/DashboardLayout';
import PageHeader from '@/components/ui/PageHeader';
import { adminSidebar } from '@/config/sidebarConfig';
import {
  type CreateTreatmentDto,
  type TreatmentRecord,
  type UpdateTreatmentDto,
  createTreatmentApi,
  deleteTreatmentApi,
  exportTreatmentsApi,
  getTreatmentsApi,
  reviewTreatmentApi,
  updateTreatmentApi,
} from '@/lib/api/gdprApi';
import { useAuthStore } from '@/stores/auth-store';
import { LegalBasis } from '@shared/enums/gdpr.enum';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, Download, Edit, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

// ─── Badge config ────────────────────────────────────────────
const LEGAL_BASIS_CONFIG: Record<string, { label: string; classes: string }> = {
  [LegalBasis.CONTRACT]: { label: 'Contrat', classes: 'bg-blue-100 text-blue-700' },
  [LegalBasis.CONSENT]: { label: 'Consentement', classes: 'bg-green-100 text-green-700' },
  [LegalBasis.LEGAL_OBLIGATION]: { label: 'Obligation légale', classes: 'bg-amber-100 text-amber-700' },
  [LegalBasis.LEGITIMATE_INTEREST]: { label: 'Intérêt légitime', classes: 'bg-purple-100 text-purple-700' },
  [LegalBasis.VITAL_INTEREST]: { label: 'Intérêt vital', classes: 'bg-red-100 text-red-700' },
  [LegalBasis.PUBLIC_TASK]: { label: 'Mission publique', classes: 'bg-slate-100 text-slate-700' },
};

function LegalBasisBadge({ value }: { value: string }) {
  const config = LEGAL_BASIS_CONFIG[value] ?? { label: value, classes: 'bg-slate-100 text-slate-700' };
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${config.classes}`}>{config.label}</span>;
}

function ReviewIndicator({ lastReviewedAt }: { lastReviewedAt?: string | null }) {
  if (!lastReviewedAt) {
    return <span className='text-sm text-red-600 font-medium'>Jamais révisé</span>;
  }
  const twelveMonthsAgo = new Date();
  twelveMonthsAgo.setFullYear(twelveMonthsAgo.getFullYear() - 1);
  const reviewDate = new Date(lastReviewedAt);
  if (reviewDate < twelveMonthsAgo) {
    return <span className='text-sm text-red-600 font-medium'>Revue expirée</span>;
  }
  return <span className='text-sm text-slate-600'>{reviewDate.toLocaleDateString('fr-FR')}</span>;
}

// ─── Empty form values ───────────────────────────────────────
const EMPTY_FORM: CreateTreatmentDto = {
  name: '',
  purpose: '',
  legalBasis: LegalBasis.LEGITIMATE_INTEREST,
  dataCategories: [],
  recipients: [],
  retentionPeriod: '',
  dpiaRequired: false,
  dpiaDate: null,
  notes: null,
};

// ─── Treatment Form Modal ────────────────────────────────────
interface TreatmentFormModalProps {
  initial?: TreatmentRecord | null;
  onClose: () => void;
  onSave: (data: CreateTreatmentDto | UpdateTreatmentDto) => void;
  isSaving: boolean;
}

function TreatmentFormModal({ initial, onClose, onSave, isSaving }: TreatmentFormModalProps) {
  const [form, setForm] = useState<CreateTreatmentDto>(() =>
    initial
      ? {
          name: initial.name,
          purpose: initial.purpose,
          legalBasis: initial.legalBasis,
          dataCategories: initial.dataCategories,
          recipients: initial.recipients,
          retentionPeriod: initial.retentionPeriod,
          dpiaRequired: initial.dpiaRequired,
          dpiaDate: initial.dpiaDate ?? null,
          notes: initial.notes ?? null,
        }
      : EMPTY_FORM,
  );

  // JSON textarea state for array fields
  const [dataCategoriesRaw, setDataCategoriesRaw] = useState(() => JSON.stringify(form.dataCategories, null, 2));
  const [recipientsRaw, setRecipientsRaw] = useState(() => JSON.stringify(form.recipients, null, 2));

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    // Parse JSON array fields
    let dataCategories: string[];
    let recipients: string[];
    try {
      dataCategories = JSON.parse(dataCategoriesRaw) as string[];
    } catch {
      toast.error('Format JSON invalide pour les catégories de données');
      return;
    }
    try {
      recipients = JSON.parse(recipientsRaw) as string[];
    } catch {
      toast.error('Format JSON invalide pour les destinataires');
      return;
    }
    if (!Array.isArray(dataCategories) || !Array.isArray(recipients)) {
      toast.error('Les champs tableau doivent être des tableaux JSON');
      return;
    }
    onSave({ ...form, dataCategories, recipients });
  }

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4'>
      <div className='w-full max-w-2xl rounded-xl bg-white shadow-2xl max-h-[90vh] overflow-y-auto'>
        <div className='sticky top-0 z-10 bg-white px-6 py-4 border-b border-slate-200'>
          <h2 className='text-lg font-bold text-slate-900'>
            {initial ? 'Modifier le traitement' : 'Nouveau traitement'}
          </h2>
        </div>
        <form onSubmit={handleSubmit} className='px-6 py-4 space-y-4'>
          {/* Name */}
          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>
              Nom du traitement <span className='text-red-500'>*</span>
            </label>
            <input
              type='text'
              required
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className='w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            />
          </div>

          {/* Purpose */}
          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>
              Finalité <span className='text-red-500'>*</span>
            </label>
            <textarea
              required
              rows={2}
              value={form.purpose}
              onChange={e => setForm(f => ({ ...f, purpose: e.target.value }))}
              className='w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            />
          </div>

          {/* Legal Basis */}
          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>
              Base légale <span className='text-red-500'>*</span>
            </label>
            <select
              value={form.legalBasis}
              onChange={e => setForm(f => ({ ...f, legalBasis: e.target.value }))}
              className='w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            >
              {Object.entries(LEGAL_BASIS_CONFIG).map(([value, { label }]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          {/* Data Categories (JSON) */}
          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>
              Catégories de données (tableau JSON)
            </label>
            <textarea
              rows={3}
              value={dataCategoriesRaw}
              onChange={e => setDataCategoriesRaw(e.target.value)}
              className='w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500'
              placeholder='["email", "nom", "adresse"]'
            />
          </div>

          {/* Recipients (JSON) */}
          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>Destinataires (tableau JSON)</label>
            <textarea
              rows={3}
              value={recipientsRaw}
              onChange={e => setRecipientsRaw(e.target.value)}
              className='w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500'
              placeholder='["équipe interne", "prestataire X"]'
            />
          </div>

          {/* Retention Period */}
          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>
              Durée de conservation <span className='text-red-500'>*</span>
            </label>
            <input
              type='text'
              required
              value={form.retentionPeriod}
              onChange={e => setForm(f => ({ ...f, retentionPeriod: e.target.value }))}
              placeholder='ex: 3 ans après fin de relation'
              className='w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            />
          </div>

          {/* DPIA Required */}
          <div className='flex items-center gap-3'>
            <input
              type='checkbox'
              id='dpiaRequired'
              checked={form.dpiaRequired}
              onChange={e => setForm(f => ({ ...f, dpiaRequired: e.target.checked }))}
              className='h-4 w-4 rounded border-slate-300'
            />
            <label htmlFor='dpiaRequired' className='text-sm font-medium text-slate-700'>
              AIPD (analyse d&apos;impact) requise
            </label>
          </div>

          {/* DPIA Date */}
          {form.dpiaRequired && (
            <div>
              <label className='block text-sm font-medium text-slate-700 mb-1'>Date de l&apos;AIPD</label>
              <input
                type='date'
                value={form.dpiaDate ?? ''}
                onChange={e => setForm(f => ({ ...f, dpiaDate: e.target.value || null }))}
                className='w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
              />
            </div>
          )}

          {/* Notes */}
          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>Notes</label>
            <textarea
              rows={2}
              value={form.notes ?? ''}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value || null }))}
              className='w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            />
          </div>

          <div className='flex justify-end gap-3 pt-2 border-t border-slate-200'>
            <button
              type='button'
              onClick={onClose}
              className='rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50'
            >
              Annuler
            </button>
            <button
              type='submit'
              disabled={isSaving}
              className='rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50'
            >
              {isSaving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────
export default function AdminGdprTreatments() {
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<TreatmentRecord | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin', 'gdpr', 'treatments', page],
    queryFn: () => getTreatmentsApi({ page, limit: 20 }),
    enabled: isAuthenticated,
  });

  const treatments = useMemo<TreatmentRecord[]>(() => data?.data ?? [], [data]);
  const meta = data?.meta;
  const isPageLoading = isLoading || !isAuthenticated;

  const createMutation = useMutation({
    mutationFn: (dto: CreateTreatmentDto) => createTreatmentApi(dto),
    onSuccess: () => {
      toast.success('Traitement créé');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'gdpr', 'treatments'] });
      setModalOpen(false);
    },
    onError: err => toast.error(err instanceof Error ? err.message : 'Erreur inconnue'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data: dto }: { id: number; data: UpdateTreatmentDto }) => updateTreatmentApi(id, dto),
    onSuccess: () => {
      toast.success('Traitement mis à jour');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'gdpr', 'treatments'] });
      setModalOpen(false);
      setEditTarget(null);
    },
    onError: err => toast.error(err instanceof Error ? err.message : 'Erreur inconnue'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteTreatmentApi(id),
    onSuccess: () => {
      toast.success('Traitement supprimé');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'gdpr', 'treatments'] });
    },
    onError: err => toast.error(err instanceof Error ? err.message : 'Erreur inconnue'),
  });

  const reviewMutation = useMutation({
    mutationFn: (id: number) => reviewTreatmentApi(id),
    onSuccess: () => {
      toast.success('Traitement marqué comme révisé');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'gdpr', 'treatments'] });
    },
    onError: err => toast.error(err instanceof Error ? err.message : 'Erreur inconnue'),
  });

  function handleExport() {
    exportTreatmentsApi()
      .then(rows => {
        const blob = new Blob([JSON.stringify(rows, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `gdpr-treatments-${new Date().toISOString().slice(0, 10)}.json`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch((err: unknown) => toast.error(err instanceof Error ? err.message : 'Export échoué'));
  }

  function handleSave(dto: CreateTreatmentDto | UpdateTreatmentDto) {
    if (editTarget) {
      updateMutation.mutate({ id: editTarget.id, data: dto });
    } else {
      createMutation.mutate(dto as CreateTreatmentDto);
    }
  }

  function handleDelete(t: TreatmentRecord) {
    if (!window.confirm(`Supprimer le traitement "${t.name}" ?`)) return;
    deleteMutation.mutate(t.id);
  }

  function openCreate() {
    setEditTarget(null);
    setModalOpen(true);
  }

  function openEdit(t: TreatmentRecord) {
    setEditTarget(t);
    setModalOpen(true);
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <DashboardLayout sidebarConfig={adminSidebar}>
      <div className='p-8 space-y-6'>
        <PageHeader
          title='Registre des traitements (Art. 30 RGPD)'
          subtitle='Inventaire de tous les traitements de données personnelles'
          actions={
            <div className='flex gap-3'>
              <button
                onClick={handleExport}
                className='flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50'
              >
                <Download className='h-4 w-4' />
                Exporter JSON
              </button>
              <button
                onClick={openCreate}
                className='flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700'
              >
                <Plus className='h-4 w-4' />
                Nouveau traitement
              </button>
            </div>
          }
        />

        {isPageLoading && (
          <div className='flex items-center justify-center py-20'>
            <div className='h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent' />
          </div>
        )}

        {isError && !isPageLoading && (
          <div className='rounded-lg bg-red-50 border border-red-200 p-4 text-red-700'>
            Erreur lors du chargement des traitements.
          </div>
        )}

        {!isPageLoading && !isError && (
          <>
            <p className={'text-xs'}>
              Ce module implémente le <strong>registre des activités de traitement</strong> requis par l'article 30 du
              RGPD (Règlement UE 2016/679).
              <br />
              Il permet au DPO / responsable de traitement de documenter, maintenir et exporter l'ensemble des
              traitements de données personnelles opérés par BoulesConnect.
            </p>
            <p className={'text-xs'}>
              <strong>Référence CNIL</strong> :{' '}
              <a
                href='https://www.cnil.fr/fr/RGPD-le-registre-des-activites-de-traitement'
                target={'_blank'}
                className={'underline'}
              >
                Le registre des activités de traitement
              </a>
            </p>
            <div className='overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm'>
              <table className='w-full text-sm'>
                <thead>
                  <tr className='border-b border-slate-200 bg-slate-50'>
                    <th className='px-4 py-3 text-left font-semibold text-slate-700'>Nom</th>
                    <th className='px-4 py-3 text-left font-semibold text-slate-700'>Base légale</th>
                    <th className='px-4 py-3 text-left font-semibold text-slate-700'>AIPD</th>
                    <th className='px-4 py-3 text-left font-semibold text-slate-700'>Dernière révision</th>
                    <th className='px-4 py-3 text-left font-semibold text-slate-700'>Version</th>
                    <th className='px-4 py-3 text-right font-semibold text-slate-700'>Actions</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-slate-100'>
                  {treatments.length === 0 && (
                    <tr>
                      <td colSpan={6} className='px-4 py-10 text-center text-slate-500'>
                        Aucun traitement enregistré.
                      </td>
                    </tr>
                  )}
                  {treatments.map(t => (
                    <tr key={t.id} className='hover:bg-slate-50/50'>
                      <td className='px-4 py-3'>
                        <p className='font-medium text-slate-900'>{t.name}</p>
                        <p className='text-xs text-slate-500 line-clamp-1'>{t.purpose}</p>
                      </td>
                      <td className='px-4 py-3'>
                        <LegalBasisBadge value={t.legalBasis} />
                      </td>
                      <td className='px-4 py-3'>
                        {t.dpiaRequired ? (
                          <span className='rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700'>
                            Requise
                          </span>
                        ) : (
                          <span className='rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500'>
                            Non requise
                          </span>
                        )}
                      </td>
                      <td className='px-4 py-3'>
                        <ReviewIndicator lastReviewedAt={t.lastReviewedAt} />
                      </td>
                      <td className='px-4 py-3 text-slate-600'>v{t.version}</td>
                      <td className='px-4 py-3'>
                        <div className='flex justify-end gap-1'>
                          <button
                            title='Marquer comme révisé'
                            onClick={() => reviewMutation.mutate(t.id)}
                            disabled={reviewMutation.isPending}
                            className='rounded p-1.5 text-slate-500 hover:bg-green-50 hover:text-green-600'
                          >
                            <CheckCircle className='h-4 w-4' />
                          </button>
                          <button
                            title='Modifier'
                            onClick={() => openEdit(t)}
                            className='rounded p-1.5 text-slate-500 hover:bg-blue-50 hover:text-blue-600'
                          >
                            <Edit className='h-4 w-4' />
                          </button>
                          <button
                            title='Supprimer'
                            onClick={() => handleDelete(t)}
                            disabled={deleteMutation.isPending}
                            className='rounded p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600'
                          >
                            <Trash2 className='h-4 w-4' />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {meta && meta.totalPages > 1 && (
              <div className='mt-4 flex items-center justify-between'>
                <p className='text-sm text-slate-600'>
                  {meta.total} traitement{meta.total > 1 ? 's' : ''} au total
                </p>
                <div className='flex gap-2'>
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className='rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40'
                  >
                    Précédent
                  </button>
                  <span className='flex items-center px-3 text-sm text-slate-600'>
                    {page} / {meta.totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(meta.totalPages, p + 1))}
                    disabled={page >= meta.totalPages}
                    className='rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40'
                  >
                    Suivant
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {modalOpen && (
          <TreatmentFormModal
            initial={editTarget}
            onClose={() => {
              setModalOpen(false);
              setEditTarget(null);
            }}
            onSave={handleSave}
            isSaving={isSaving}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
